import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { US_STATES } from "@/lib/usStates";
import { Sparkline } from "@/components/Sparkline";
import { AmenityRadar } from "@/components/AmenityRadar";
import api from "@/lib/api";

export default function Trends() {
  const [state, setState] = useState("");
  const [data, setData] = useState(null);
  const [view, setView] = useState("single"); // 'single' | 'all'
  const [byState, setByState] = useState(null);

  const load = async (st) => {
    try {
      const { data } = await api.get("/trends/amenities", { params: st ? { state: st } : {} });
      setData(data);
    } catch (err) { console.error(err); }
  };

  const loadAllStates = async () => {
    try {
      const { data } = await api.get("/trends/by-state");
      setByState(data.states || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { load(""); }, []);
  useEffect(() => { if (view === "all" && byState === null) loadAllStates(); }, [view, byState]);

  const stateName = US_STATES.find((s) => s.code === state)?.name;

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-14">
        <div className="tick-label">Public market data</div>
        <h1 className="font-display font-black text-5xl tracking-tighter mt-2">What sells in each state.</h1>
        <p className="text-neutral-400 mt-3 max-w-xl">
          Thinking of listing? See what the top-equipped kitchens have before you spend a dollar on gear.
        </p>

        <div className="mt-8 flex gap-2 flex-wrap items-center">
          <div className="border hairline flex" data-testid="trends-view-toggle">
            <button
              data-testid="trends-view-single"
              onClick={() => setView("single")}
              className={`px-4 py-2.5 tick-label transition-colors ${view === "single" ? "bg-[#FF3B30] text-white" : "text-neutral-400 hover:text-white"}`}
            >One state</button>
            <button
              data-testid="trends-view-all"
              onClick={() => setView("all")}
              className={`px-4 py-2.5 tick-label transition-colors ${view === "all" ? "bg-[#FF3B30] text-white" : "text-neutral-400 hover:text-white"}`}
            >All states</button>
          </div>
          {view === "single" && (
            <select data-testid="trends-state" value={state} onChange={(e) => { setState(e.target.value); load(e.target.value); }} className="border hairline px-4 py-2.5 focus:outline-none focus:border-[#FF3B30]">
              <option value="">Nationwide</option>
              {US_STATES.map((s) => (<option key={s.code} value={s.code}>{s.code} · {s.name}</option>))}
            </select>
          )}
        </div>

        {view === "single" && data && (
          <div className="mt-8 border hairline p-5" data-testid="public-trends">
            <div className="tick-label mb-3">{data.total_kitchens} kitchens · {stateName || data.state || "all states"}</div>
            {data.top.length === 0 ? (
              <div className="text-neutral-500">No amenity data yet.</div>
            ) : (
              <div className="space-y-2">
                {data.top.map((row) => {
                  const pct = data.total_kitchens ? Math.round(row.count / data.total_kitchens * 100) : 0;
                  return (
                    <div key={row.amenity} className="flex items-center gap-3">
                      <div className="w-40 text-sm truncate">{row.amenity}</div>
                      <div className="flex-1 bg-neutral-900 border hairline h-5 overflow-hidden">
                        <div className="h-full bg-[#FF3B30]" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="font-mono text-sm w-20 text-right">{row.count} · {pct}%</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {view === "all" && (
          <div className="mt-8" data-testid="trends-by-state">
            {byState === null ? (
              <div className="tick-label">Loading all states...</div>
            ) : byState.length === 0 ? (
              <div className="text-neutral-500 border hairline p-6">No state data yet — add kitchens with a state to see trends.</div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {byState.map((row) => {
                  const name = US_STATES.find((s) => s.code === row.state)?.name || row.state;
                  return (
                    <div key={row.state} data-testid={`trend-state-${row.state}`} className="border hairline p-4">
                      <div className="flex items-baseline justify-between">
                        <div className="font-display font-bold text-lg">{name}</div>
                        <div className="font-mono text-xs text-neutral-500">{row.total_kitchens} kitchens</div>
                      </div>
                      {row.top3.length === 0 ? (
                        <div className="text-neutral-600 text-sm mt-3">No amenities listed yet.</div>
                      ) : (
                        <ol className="mt-3 space-y-1 text-sm">
                          {row.top3.map((a, i) => (
                            <li key={a.amenity} className="flex items-center gap-2">
                              <span className="font-mono text-neutral-500 w-4">{i + 1}.</span>
                              <span className="flex-1 truncate">{a.amenity}</span>
                              <span className="font-mono text-neutral-500 text-xs">{a.count}</span>
                            </li>
                          ))}
                        </ol>
                      )}
                      <div className="mt-3 flex items-start justify-between gap-2 border-t hairline pt-3">
                        <div>
                          <div className="tick-label">30-day trend</div>
                          <Sparkline data={row.sparkline || []} />
                        </div>
                        <div className="flex-shrink-0">
                          <div className="tick-label mb-1 text-right">vs Nation</div>
                          <AmenityRadar data={row.top3 || []} />
                          {row.top3.length >= 3 && (() => {
                            const wins = row.top3.filter((a) => (a.share || 0) > (a.nation_share || 0)).length;
                            const hot = wins >= 2;
                            return (
                              <div
                                data-testid={`radar-rank-${row.state}`}
                                className="tick-label text-right mt-1"
                                style={{ color: hot ? "#FF3B30" : "rgba(255,255,255,0.55)" }}
                              >
                                Wins {wins}/3
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
